/**
 * TanStack Query hook for the Microsoft 365 Copilot adoption section of the
 * AI board.
 *
 * One route, org-level aggregates only: `/copilot/overview` carries tenant-wide
 * enabled/active counts, a daily trend and per-app counts — no username, UPN or
 * id exists anywhere in the payload, by construction (the backend can only
 * build the two org-count Graph report URLs, and its route takes no
 * parameters).
 *
 * The route never fails because Microsoft Graph is unavailable, unauthorised or
 * retired: a missing credential, a 403, a 404, a 400 and a 5xx are all *fields*
 * on a 200 (`configured` / `access_state` / `status_detail` /
 * `missing_permissions` / `errors`). A thrown error from this hook therefore
 * means the **NOVA** API is down, not Graph.
 *
 * **The poll interval comes from the payload, not from a constant here.** The
 * response carries `cache_ttl_sec` — an hour on a healthy read, because the
 * upstream report only refreshes daily; a minute on a degraded one, so a
 * consent landing is noticed quickly. Polling faster than the backend rebuilds
 * just re-serves identical bytes. Same reasoning as `./ai.ts`, `./fo.ts` and
 * `./defender.ts`.
 *
 * **Nothing here is persisted.** `../persist.ts` is a deny-by-default
 * allow-list and `["copilot", …]` is deliberately absent from it: adoption
 * figures describe the tenant's licensing posture and do not belong in browser
 * storage.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching every other hook file: the barrel re-exports
 * these hooks, so importing it here would close an import cycle.
 */

import { useQuery } from "@tanstack/react-query";

import { getCopilotOverview } from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the Copilot adoption payload. */
export const COPILOT_OVERVIEW_KEY = ["copilot", "overview"] as const;

/**
 * Interval used only before the first response arrives, in ms.
 *
 * Five minutes: shorter than the healthy TTL, so the first refetch can never be
 * slower than the backend's own rebuild. After that the payload's own
 * `cache_ttl_sec` takes over.
 */
const FALLBACK_INTERVAL_MS = 300_000;

/**
 * Refetch on the cadence the payload itself declares.
 *
 * Takes the *data* rather than the query so it cannot feed back into
 * TanStack's inference of the query's own data type.
 */
function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/**
 * Org-level Microsoft 365 Copilot adoption: enabled (licensed) vs active
 * users, the daily trend, and per-app counts.
 *
 * Read `summary` through `access_state` and never as bare numbers: `null` is a
 * read that failed or a report with no rows, a `0` is a measured zero — a
 * licensed-but-idle app is the finding this section exists to surface.
 */
export function useCopilotOverview() {
  return useQuery({
    queryKey: COPILOT_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getCopilotOverview();
      // Counts and states only — the payload holds nothing else, and a log
      // line must stay that way even if the payload ever grows.
      logger.debug("[copilot] overview received", {
        accessState: data?.access_state,
        enabledUsers: data?.summary?.enabled_users,
        activeUsers: data?.summary?.active_users,
        apps: data?.apps.length ?? 0,
        trendDays: data?.trend.length ?? 0,
        errors: data?.errors.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}
